import React, { useEffect, useState, useCallback } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, Loader2, User } from "lucide-react";

export default function DrepturiPage() {
  const [users, setUsers] = useState([]);
  const [ecrane, setEcrane] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ email: "", nume: "", password: "", permisiuni: {} });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ec, us] = await Promise.all([apiGet("/auth/ecrane"), apiGet("/auth/users")]);
      setEcrane(ec.ecrane || []);
      setUsers(us.items || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiPost("/auth/users", form);
      toast.success("Operator adaugat");
      setForm({ email: "", nume: "", password: "", permisiuni: {} });
      await load();
    } catch (ex) {
      toast.error(ex.message);
    } finally {
      setSaving(false);
    }
  };

  const togglePerm = async (u, ecranId) => {
    const permisiuni = { ...(u.permisiuni || {}), [ecranId]: !(u.permisiuni || {})[ecranId] };
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, permisiuni } : x)));
    try {
      await apiPut(`/auth/users/${u.id}`, { permisiuni });
    } catch (ex) {
      toast.error(ex.message);
      load();
    }
  };

  const toggleActiv = async (u) => {
    try {
      const updated = await apiPut(`/auth/users/${u.id}`, { activ: !u.activ });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
    } catch (ex) {
      toast.error(ex.message);
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Stergeti contul ${u.email}?`)) return;
    try {
      await apiDelete(`/auth/users/${u.id}`);
      toast.success("Cont sters");
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch (ex) {
      toast.error(ex.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="drepturi-page">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-amber-600" />
        <h1 className="text-2xl font-bold text-slate-900">Drepturi si utilizatori</h1>
      </div>

      <form
        onSubmit={createUser}
        className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm grid grid-cols-1 md:grid-cols-4 gap-3 items-end"
        data-testid="add-operator-form"
      >
        <div>
          <label className="block text-slate-600 text-xs mb-1 uppercase tracking-wide">Email</label>
          <input
            type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="op-email"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
            placeholder="operator@amel.ro"
          />
        </div>
        <div>
          <label className="block text-slate-600 text-xs mb-1 uppercase tracking-wide">Nume</label>
          <input
            value={form.nume}
            onChange={(e) => setForm({ ...form, nume: e.target.value })}
            data-testid="op-nume"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
            placeholder="Nume operator"
          />
        </div>
        <div>
          <label className="block text-slate-600 text-xs mb-1 uppercase tracking-wide">Parola</label>
          <input
            type="password" required value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="op-password"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
            placeholder="min. 6 caractere"
          />
        </div>
        <button
          type="submit" disabled={saving}
          data-testid="op-submit"
          className="rounded-md bg-slate-900 hover:bg-slate-800 text-white font-medium px-4 py-2 text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          Adauga operator
        </button>
      </form>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Se incarca...
          </div>
        ) : (
          <table className="text-sm border-collapse" data-testid="users-table">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left font-medium text-slate-600 min-w-[180px] border-r border-slate-200">
                  Ecran / Utilizator
                </th>
                {users.map((u) => (
                  <th key={u.id} className="px-3 py-3 align-bottom min-w-[130px] bg-slate-50" data-testid={`user-col-${u.email}`}>
                    <div className="flex flex-col items-center gap-1.5">
                      <div className="flex items-center gap-1.5">
                        <User className="h-4 w-4 text-slate-400 shrink-0" />
                        <span className="font-medium text-slate-900 truncate max-w-[100px]" title={u.nume || u.email}>
                          {u.nume || u.email}
                        </span>
                      </div>
                      <div className="text-slate-500 text-[11px] truncate max-w-[120px]" title={u.email}>{u.email}</div>
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${u.rol === "admin" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                        {u.rol === "admin" ? "Admin" : "Operator"}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ecrane.map((ec) => (
                <tr key={ec.id} className="border-b border-slate-100" data-testid={`ecran-row-${ec.id}`}>
                  <td className="sticky left-0 z-10 bg-white px-4 py-3 font-medium text-slate-800 border-r border-slate-200">
                    {ec.label}
                  </td>
                  {users.map((u) => (
                    <td key={u.id} className="px-3 py-3 text-center">
                      {u.rol === "admin" ? (
                        <span className="text-amber-600" title="Acces complet">✓</span>
                      ) : (
                        <input
                          type="checkbox"
                          checked={!!(u.permisiuni || {})[ec.id]}
                          onChange={() => togglePerm(u, ec.id)}
                          data-testid={`perm-${u.email}-${ec.id}`}
                          className="h-4 w-4 accent-amber-500 cursor-pointer"
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-b border-slate-100 bg-slate-50/60" data-testid="row-activ">
                <td className="sticky left-0 z-10 bg-slate-50/60 px-4 py-3 font-medium text-slate-800 border-r border-slate-200">
                  Activ
                </td>
                {users.map((u) => (
                  <td key={u.id} className="px-3 py-3 text-center">
                    {u.rol === "admin" ? (
                      <span className="text-slate-400 text-xs">—</span>
                    ) : (
                      <button
                        onClick={() => toggleActiv(u)}
                        data-testid={`toggle-activ-${u.email}`}
                        className={`px-2 py-0.5 rounded text-xs font-medium ${u.activ ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                      >
                        {u.activ ? "Activ" : "Inactiv"}
                      </button>
                    )}
                  </td>
                ))}
              </tr>
              <tr data-testid="row-actiuni">
                <td className="sticky left-0 z-10 bg-white px-4 py-3 font-medium text-slate-800 border-r border-slate-200">
                  Actiuni
                </td>
                {users.map((u) => (
                  <td key={u.id} className="px-3 py-3 text-center">
                    {u.rol !== "admin" ? (
                      <button
                        onClick={() => removeUser(u)}
                        data-testid={`delete-user-${u.email}`}
                        className="text-red-500 hover:text-red-700 transition-colors"
                        title="Sterge"
                      >
                        <Trash2 className="h-4 w-4 mx-auto" />
                      </button>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
